import * as myDevice from "@/helpers/deviceHelpers"
import IamService from "@/services/iamService"
import { defineStore, getActivePinia } from 'pinia'
import { IUser } from "@/interface/services/response/user.interface"
import { ILoginPayload } from "@/interface/services/request/user-payload.interface"
import { ITokenExpiredPayload } from "@/interface/services/request/token-payload.interface"
import { UserRoleEnum } from '@/enums'
import { AxiosError } from 'axios'
import Cookies from 'quasar/src/plugins/Cookies.js';
import { dateAddMonth, getEnumKeyByEnumValue } from '@/mega-ui/helpers/function'
import { useAbortController } from "@/composables/useAbortController"
import { ExtendedPinia } from "@/interface"
import { useAlertModals } from "@/composables/useAlertModals"
import { AlertModalEnum } from "@/enums/common/alert-modal.enum"
import { useSetLangQuasar } from "@/composables/useSetLangQuasar"
import { getContactStore, getCustomerStore } from "@/helpers/storeHelpers"
import { isSilentAuthAllowed } from "@/helpers"

const accessTokenCookieName = 'my_access_token'
const refreshTokenCookieName = 'my_refresh_token'

const accessTokenCookie = {
    setItem (_key, state) {
        return Cookies.set(accessTokenCookieName, JSON.parse(state)['access_token'] ?? '', { expires: 1, secure: (process.env.NODE_ENV === 'production'), sameSite: 'Strict' })
    },
    getItem (_key) {
        return JSON.stringify({
            access_token: Cookies.get(accessTokenCookieName),
        })
    },
} as Storage

const refreshTokenCookie = {
    setItem (_key, state) {
        return Cookies.set(refreshTokenCookieName, JSON.parse(state)['refresh_token'] ?? '', { expires: dateAddMonth(new Date(), 3), secure: (process.env.NODE_ENV === 'production'), sameSite: 'Strict' })
    },
    getItem (_key) {
        return JSON.stringify({
            refresh_token: Cookies.get(refreshTokenCookieName),
        })
    },
} as Storage

export const useAuthenticationStore = defineStore('authentication', {
    state: (): IAuthState => ({
        access_token: '',
        refresh_token: '',
        user: null
    }),
    actions: {
        async login(login: string, password: string): Promise<number> {
            const payload = <ILoginPayload>{}
            payload.login = login
            payload.password = password
            if (isSilentAuthAllowed()) {
                payload.deviceId = myDevice.getDeviceId()
                payload.deviceKey = myDevice.getDeviceKey()
            }
            
            try {
                const response = await IamService.login(payload)
                this.logout() // Clear all stores to wipe data
                if (!response.user || !response.token) {
                    return 401
                }
                if (response.user && (response.user.scope === 'LEGACY' || !response.user.customer_id)) {
                    return 403
                } 
                
                this.access_token = response.token
                this.refresh_token = response.refresh_token ?? ''
                this.user = response.user
                return 200
            }
            catch (error) {
                if (error instanceof AxiosError) {
                    return error.response ? error.response.status : -1
                }

                return 500
            }
        },
        async loginUuid(uuid: string, token: string): Promise<number> {
            try {
                const response = await IamService.loginUuid(uuid, token)
                this.access_token = response.token
                this.refresh_token = response.refresh_token ?? ''
                this.user = response.user
                return 200
            }
            catch (error) {
                if (error instanceof AxiosError) {
                    return error.response ? error.response.status : -1
                }

                return 500
            }
        },
        async renewAccess(uuid: string|null, resetOnNextError = false): Promise<number> {
            try {
                if (!uuid || !this.refresh_token || this.refresh_token === '') {
                    return 500
                }

                const response = await IamService.renewAccess({ 
                    refresh_token: this.refresh_token,
                    deviceKey: myDevice.getDeviceKey()
                })

                this.access_token = response.token ?? ''
                this.refresh_token = response.refresh_token ?? ''
                this.user = response.user
                return 200
            }
            catch (error) {
                if (error instanceof AxiosError) {
                    const status = error.response ? error.response.status : -1
                    if (resetOnNextError || [419, 429].includes(status)) {
                        // Force a reset of the refresh token if: too many attempts or 
                        // if the token expired or if 'resetOnNextError' == true.
                        this.refresh_token = ''
                    }

                    return error.response ? error.response.status : -1
                }

                return 500
            }
        },
        async checkExpired() : Promise<boolean> {
            const payload = <ITokenExpiredPayload>{}
            payload.token = this.access_token ? this.access_token : ''

            const response = await IamService.getTokenStatus(payload)
            return response.expired;
        },
        removeRefreshToken() {
            this.refresh_token = ''

            // Remove the cookie.
            if (Cookies.has(refreshTokenCookieName)) {
                Cookies.remove(refreshTokenCookieName)
            }
        },
        updateLanguage(value: string) {
            useSetLangQuasar(this, value);
            if (this.user) {
                if (this.user.language) {
                    this.user.language.app = value
                } else {
                    this.user.language = { app: value }
                }
            }
        },
        updateUser(user: IUser) {
            this.user = user
        },
        logout(onlyRemoveAccessToken = false, resetKey = false) {
            // Abort cancellable requests.
            const abortController = useAbortController()
            abortController.abortRequests()
            abortController.destroyController()

            // Remove access token to allow redirecting to 'enter PIN code' page 
            if (onlyRemoveAccessToken) {
                this.access_token = ''

                // To be sure we also remove the cookie.
                Cookies.remove(accessTokenCookieName)
            } else {
                // Clear alert modal states.
                const modals = useAlertModals(Object.values(AlertModalEnum))
                modals.clearStates()
                
                // Clear stores.
                const activePinia = getActivePinia() as ExtendedPinia
                activePinia?._s.forEach((store) => store.$reset())
                myDevice.clearStorage()

                // Forced logout => also remove the cookies.
                if (Cookies.has(accessTokenCookieName)) {
                    Cookies.remove(accessTokenCookieName)
                }
                if (Cookies.has(refreshTokenCookieName)) {
                    Cookies.remove(refreshTokenCookieName)
                }

                // Refresh the device key after logout.
                if (resetKey) {
                    myDevice.setDeviceKey(true)
                }
            }
        }
    },
    getters: {
        userId(state: IAuthState) {
            return state.user?.id
        },
        userGuid(state: IAuthState) {
            return state.user?.guid
        },
        customerId(state: IAuthState) {
            return state.user?.customer_id
        },
        customer(_state: IAuthState) {
            return getCustomerStore()
        },
        contact(_state: IAuthState) {
            return getContactStore()
        },
        customerSpaceUrl(state: IAuthState) {
            if (!this.isAuthenticated){
                return undefined 
            }

            return `${process.env.VUE_APP_URL_ESPACE_CLIENT}/${state.user?.guid}`
        },
        energyRef(state: IAuthState) {
            if (this.customer && !this.customer.energyLinkConfirmed) {
                return undefined 
            }

            return state.user?.energy_ref
        },
        hasEnergy(state: IAuthState): boolean {
            if (this.customer && !this.customer.energyLinkConfirmed) {
                return false 
            }

            return !(!state.user || !state.user.energy_ref || state.user.energy_ref === '');
        },
        isLegacy(state: IAuthState): boolean {
            return state.user?.scope === 'LEGACY'
        },
        isTemp(state: IAuthState): boolean {
            return state.user?.scope === 'TEMP'
        },
        lang(state: IAuthState) {
            return state.user?.language?.app
        },
        msisdn(state: IAuthState) {
            return state.user?.msisdn
        },
        isAuthenticated(state: IAuthState): boolean {
            return state.access_token != null && state.access_token !== ''
        },
        isAdmin(state: IAuthState): boolean {
            return state.user?.role === UserRoleEnum.ADMIN
        },
        isOwner(state: IAuthState): boolean {
            return state.user?.role === UserRoleEnum.OWNER
        },
        isUser(state: IAuthState): boolean {
            return state.user?.role === UserRoleEnum.USER
        },
        isProRestriction(state: IAuthState): boolean {
            return (state.user?.role === UserRoleEnum.ADMIN || state.user?.role === UserRoleEnum.OWNER) && !this.contact?.isResidential
        },
        userHasPersonalData(_state: IAuthState) : boolean {
            return !!(this.user && this.user.personal_data);
        },
        userFirstLastName(_state: IAuthState) : string|null {
            const personalData = this.user?.personal_data ?? null
            if (personalData) {
                const value = (personalData.first_name ? personalData.first_name + ' ' : '') + (personalData.last_name ?? '')
                return value == '' ? null : value 
            }

            return null
        },
        userLastFirstName(_state: IAuthState) : string|null {
            const personalData = this.user?.personal_data ?? null
            if (personalData) {
                const value = (personalData.last_name ? personalData.last_name + ' ' : '') + (personalData?.first_name ?? '')
                return value == '' ? null : value 
            }

            return null
        },
        userBirthDate(_state: IAuthState) : string|null {
            const personalData = this.user?.personal_data ?? null
            if (personalData) {
                return personalData.birth_date
            }

            return null
        },
        userEmail(_state: IAuthState) : string|null {
            if (this.user && this.user.email) {
                return this.user.email
            }

            return null
        },        
        userMsisdn(_state: IAuthState) : string|null {
            if (this.user && this.user.msisdn) {
                return this.user.msisdn
            }

            return null
        },
        userRole(_state: IAuthState) : UserRoleEnum {
            if (this.user?.role) {
                const key = getEnumKeyByEnumValue(UserRoleEnum, this.user?.role)
                return UserRoleEnum[key as keyof typeof UserRoleEnum]
            }
            
            return UserRoleEnum.NONE
        }
    },
    persist: [
        {
            paths: ['user'],
            storage: localStorage,
        },
        {
            paths: ['access_token'],
            storage: accessTokenCookie,
        },
        {
            paths: ['refresh_token'],
            storage: refreshTokenCookie,
        },
    ],
})

interface IAuthState {
    access_token: string,
    refresh_token: string,
    user?: IUser|null
}

export type AuthStore = ReturnType<typeof useAuthenticationStore>